import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Percent, DollarSign, Receipt } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import apiClient from '@/services/api';
import type { TaxReport as TaxReportData } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/**
 * Tax: per-tax-rate breakdown mirroring the POS Tax Report — one row per rate
 * (Items / Tax / Total / VAT / Excl. VAT) with Tax Total, System Total and the
 * Excl-VAT total, plus an optional per-day breakdown ("show daily" on the POS).
 */
export default function TaxReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;
  const [showDaily, setShowDaily] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<TaxReportData>({
    queryKey: ['tax-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getTaxReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const rows = data?.rows ?? [];
  const byDay = data?.byDay ?? [];
  const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  // POS column order: NR (items), NAME, TOTAL (gross), VAT, EXCL. VA (net).
  const rateHeaders = ['Items', 'Tax', 'Total', 'VAT', 'Excl. VAT'];
  const rateRows = rows.map((r) => [
    num(r.itemCount),
    r.taxName,
    formatCurrency(r.totalAmount),
    formatCurrency(r.taxAmount),
    formatCurrency(r.exclTax),
  ]);
  // Totals row (POS master band: Tax Total / System Total / Excl-VA total).
  const totalsRow = data
    ? [num(data.totalItemCount), 'TOTAL', formatCurrency(data.totalAmount), formatCurrency(data.totalTax), formatCurrency(data.totalExclTax)]
    : [];

  const dailyHeaders = ['Date', 'Items', 'Tax', 'Total', 'VAT', 'Excl. VAT'];
  const dailyRows = byDay.map((d) => [
    format(parseISO(d.date), 'yyyy-MM-dd'),
    num(d.itemCount),
    d.taxName,
    formatCurrency(d.totalAmount),
    formatCurrency(d.taxAmount),
    formatCurrency(d.exclTax),
  ]);

  const chart = rows.map((r) => ({ name: r.taxName, vat: Number(r.taxAmount) }));

  const sections = (): ReportSection[] => {
    const s: ReportSection[] = [
      { heading: 'Tax by Rate', headers: rateHeaders, rows: [...rateRows, totalsRow] },
    ];
    if (showDaily) s.push({ heading: 'Tax by Day', headers: dailyHeaders, rows: dailyRows });
    return s;
  };
  const kpis = (): [string, string][] => [
    ['Tax Total', formatCurrency(data?.totalTax ?? 0)],
    ['System Total', formatCurrency(data?.totalAmount ?? 0)],
    ['Excl. VAT', formatCurrency(data?.totalExclTax ?? 0)],
  ];

  return (
    <ReportFrame
      title="Tax"
      description="Tax collected per tax rate across the range (NR / NAME / TOTAL / VAT / EXCL. VA), matching the POS Tax Report."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={rows.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`tax-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Tax', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Tax Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-3 gap-4">
        <KPICard label="Tax Total" value={formatCurrency(data?.totalTax ?? 0)} icon={Percent} palette="red" />
        <KPICard label="System Total" value={formatCurrency(data?.totalAmount ?? 0)} icon={DollarSign} palette="green" />
        <KPICard label="Excl. VAT" value={formatCurrency(data?.totalExclTax ?? 0)} icon={Receipt} palette="blue" />
      </div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">VAT by Tax Rate</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="vat" name="VAT" fill="#FF8042" />
          </BarChart>
        </ResponsiveContainer>
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Tax by Rate</h3>
        <SimpleTable headers={rateHeaders} rows={[...rateRows, totalsRow]} />
      </div></div>

      <div className="card"><div className="card-body">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Tax by Day</h3>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={showDaily} onChange={(e) => setShowDaily(e.target.checked)} />
            Show daily
          </label>
        </div>
        {showDaily
          ? <SimpleTable headers={dailyHeaders} rows={dailyRows} />
          : <p className="text-sm text-gray-500">Enable “Show daily” to break the figures down by day and tax rate.</p>}
      </div></div>
    </ReportFrame>
  );
}
